import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Mail, Send, Brain, RefreshCw, PenLine, Sparkles, X, Paperclip, Type, MinusCircle } from 'lucide-react'
import { sendGmailEmail } from '../../lib/email'
import { supabase } from '../../lib/supabase'

export interface EmailReplyComposerProps {
  businessId: string
  to: string
  /** Comma-separated Cc recipients to send with the message. */
  cc?: string
  /** Comma-separated Bcc recipients to send with the message. */
  bcc?: string
  /** Initial HTML body (e.g. forwarded message content). */
  defaultBody?: string
  defaultSubject?: string
  placeholder?: string
  onSendSuccess?: (messageId: string, details?: { subject: string; body: string; to: string }) => void
  onSendError?: (error: string) => void
  disabled?: boolean
  className?: string
  showSubjectInput?: boolean
  subjectReadOnly?: boolean
  variant?: 'default' | 'gmail'
  // AI context props
  conversationId?: string
  customerId?: string
  prefetchedSuggestion?: string
  shouldFetchAI?: boolean // NEW: Control whether to fetch AI suggestions
  onAIStateChange?: (isLoading: boolean, error: string | null, isAiText: boolean) => void // NEW: Callback for AI state changes
  onRefreshAI?: (refreshHandler: () => void) => void // NEW: Callback to expose AI refresh handler
  onInsertContent?: (insertFn: (content: string, replace?: boolean) => void) => void // NEW: Callback to expose content insertion with replace option
  // AI compose props
  onAICompose?: (emailBody: string, subject: string) => void // NEW: Callback when AI compose button is clicked, passes current email body and subject
  onSubjectInsert?: (insertFn: (subject: string) => void) => void // NEW: Callback to expose subject insertion
  isAIGenerating?: boolean // NEW: Show AI generation overlay animation
  toolbarPosition?: 'inline' | 'bottom'
  /** Gmail thread ID so the reply stays in the same thread (from received_emails lookup by gmail_id) */
  threadId?: string
  /** In-Reply-To header for the message being replied to */
  inReplyTo?: string
  /** References header for threading */
  references?: string
}

interface AISuggestion {
  text: string;
  tone: string;
}

export function EmailReplyComposer({
  businessId,
  to,
  cc,
  bcc,
  defaultBody = '',
  defaultSubject = '',
  placeholder = 'Type your email message here...',
  onSendSuccess,
  onSendError,
  disabled = false,
  className = '',
  showSubjectInput = true,
  subjectReadOnly = false,
  variant = 'default',
  conversationId,
  customerId,
  prefetchedSuggestion,
  shouldFetchAI = true, // Default to true for backward compatibility
  onAIStateChange, // New prop
  onRefreshAI, // New prop
  onInsertContent, // New prop
  onAICompose, // New prop
  onSubjectInsert, // New prop
  isAIGenerating = false, // New prop
  toolbarPosition = 'inline',
  threadId,
  inReplyTo,
  references
}: EmailReplyComposerProps) {
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState(defaultBody || '')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailSignature, setEmailSignature] = useState('')
  const [signatureIncluded, setSignatureIncluded] = useState(true)
  
  // Attachment state
  const [attachments, setAttachments] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // AI suggestion state
  const [aiSuggestion, setAiSuggestion] = useState<string>('')
  const [isAiText, setIsAiText] = useState(false)
  const [isLoadingAI, setIsLoadingAI] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  // Refs for request management
  const currentRequestRef = useRef<AbortController | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastRequestKeyRef = useRef<string>('')
  const bodyEditorRef = useRef<HTMLDivElement>(null)
  const formatBarRef = useRef<HTMLDivElement>(null)
  const ignoreBodySyncRef = useRef(false)

  // Format bar (B/I/U) visibility and active state (stays selected until toggled off)
  const [showFormatBar, setShowFormatBar] = useState(false)
  const [formatActive, setFormatActive] = useState({ bold: false, italic: false, underline: false })
  const formatAreaRef = useRef<HTMLDivElement>(null)

  // One-click Polish: clean up the existing draft in place — nothing more.
  // Replaces the old prompt-to-compose flow on the sparkles button.
  const [isPolishing, setIsPolishing] = useState(false)
  const handlePolish = async () => {
    if (!supabase || isPolishing) return
    const currentBody = (body || '').trim()
    if (!currentBody) return
    setIsPolishing(true)
    setError(null)
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('ai-compose-email', {
        body: {
          prompt: `Polish and enhance this email message, making it more professional and clear: "${currentBody}"`,
          mode: 'polish',
          hasSubject: false,
          businessId,
        },
      })
      if (data?.insufficientTokens) {
        setError('Not enough AI tokens — purchase more to use Polish.')
        return
      }
      if (invokeError) throw invokeError
      if (!data?.success || !data?.email) {
        throw new Error(data?.error || 'Failed to polish the message')
      }
      // Plain-text responses need their line breaks preserved as HTML.
      const polished = /<[a-z][\s\S]*>/i.test(data.email)
        ? data.email
        : String(data.email).replace(/\n/g, '<br>')
      setBody(polished) // body→editor sync effect pushes this into the contenteditable
      setIsAiText(true)
    } catch (err) {
      console.error('Error polishing email:', err)
      setError(err instanceof Error ? err.message : 'Failed to polish the message')
    } finally {
      setIsPolishing(false)
    }
  }

  // Prefill the editor with an initial body (e.g. forwarded content) once on mount.
  const didInitBodyRef = useRef(false)
  useEffect(() => {
    if (didInitBodyRef.current) return
    if (defaultBody && bodyEditorRef.current) {
      bodyEditorRef.current.innerHTML = defaultBody
      setBody(defaultBody)
      didInitBodyRef.current = true
    }
  }, [defaultBody])

  const updateFormatStateFromSelection = useCallback(() => {
    const el = bodyEditorRef.current
    if (!el || typeof document.getSelection === 'undefined') return
    const sel = document.getSelection()
    if (!sel || sel.rangeCount === 0) return
    if (!el.contains(sel.anchorNode)) return
    setFormatActive({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline')
    })
  }, [])

  const applyFormat = (command: 'bold' | 'italic' | 'underline') => {
    bodyEditorRef.current?.focus()
    document.execCommand(command, false)
    if (bodyEditorRef.current) {
      let html = bodyEditorRef.current.innerHTML
      if (html === '<br>' || html === '<br/>' || html === '<div><br></div>') html = ''
      setBody(html)
    }
    setHasUserInteracted(true)
    setIsAiText(false)
    setFormatActive({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline')
    })
  }

  useEffect(() => {
    setSubject(defaultSubject)
    setSignatureIncluded(true)
  }, [defaultSubject])

  // Sync external body (insert/AI) into contenteditable
  useEffect(() => {
    if (ignoreBodySyncRef.current) {
      ignoreBodySyncRef.current = false
      return
    }
    const el = bodyEditorRef.current
    const targetHtml = body || '<br>'
    if (el && body !== undefined && el.innerHTML !== targetHtml) {
      el.innerHTML = targetHtml
    }
  }, [body])

  // Close format bar when clicking outside
  useEffect(() => {
    if (!showFormatBar) return
    const onMouseDown = (e: MouseEvent) => {
      if (formatAreaRef.current?.contains(e.target as Node)) return
      setShowFormatBar(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [showFormatBar])

  // Keep B/I/U selection state in sync with editor (selection or cursor)
  useEffect(() => {
    const onSelectionChange = () => {
      if (!bodyEditorRef.current) return
      const sel = document.getSelection()
      if (sel && sel.rangeCount > 0 && bodyEditorRef.current.contains(sel.anchorNode)) {
        updateFormatStateFromSelection()
      }
    }
    document.addEventListener('selectionchange', onSelectionChange)
    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, [updateFormatStateFromSelection])

  // When opening the format bar, reflect current selection state
  useEffect(() => {
    if (showFormatBar) updateFormatStateFromSelection()
  }, [showFormatBar, updateFormatStateFromSelection])

  // Load email signature from Messages app settings (used when sending and shown below composer)
  useEffect(() => {
    if (!businessId || !supabase) return
    let cancelled = false
    const load = async () => {
      try {
        const { data, error } = await supabase.rpc('get_app_setting', {
          p_business_id: businessId,
          p_app_slug: 'messages',
          p_setting_key: 'email_signature',
          p_default_value: '{}'
        })
        if (cancelled) return
        if (!error && data && typeof data === 'object' && data !== null && 'body' in data) {
          setEmailSignature(String((data as { body?: string }).body ?? '').trim())
        } else {
          setEmailSignature('')
        }
      } catch {
        if (!cancelled) setEmailSignature('')
      }
    }
    load()
    return () => { cancelled = true }
  }, [businessId])

  // Expose content insertion function (works with contenteditable; content can be plain text or HTML)
  useEffect(() => {
    if (onInsertContent) {
      onInsertContent((content: string, replace: boolean = false) => {
        const html = /<[a-z][\s\S]*>/i.test(content) ? content : formatBodyAsHtml(content);
        const el = bodyEditorRef.current;
        if (replace) {
          setBody(html);
          if (el) el.innerHTML = html;
        } else {
          if (el) {
            el.focus();
            document.execCommand('insertHTML', false, html);
            let h = el.innerHTML;
            if (h === '<br>' || h === '<br/>' || h === '<div><br></div>') h = '';
            setBody(h);
          } else {
            setBody(prev => prev ? prev + html : html);
          }
        }
        setIsAiText(false);
        setHasUserInteracted(true);
      });
    }
  }, [onInsertContent]);

  // Expose subject insertion function
  useEffect(() => {
    if (onSubjectInsert) {
      onSubjectInsert((newSubject: string) => {
        setSubject(newSubject);
      });
    }
  }, [onSubjectInsert]);

  // Clear error when user starts typing
  useEffect(() => {
    if ((subject || body) && error) {
      setError(null)
    }
  }, [subject, body, error])

  // Create a debounced fetch function
  const debouncedFetchAISuggestion = useCallback(async () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Create a unique key for this request
    const requestKey = `${businessId}-${conversationId}-${customerId}-${to}`
    
    // Don't make the same request twice
    if (requestKey === lastRequestKeyRef.current && !hasUserInteracted) {
      return
    }

    timeoutRef.current = setTimeout(async () => {
      if (!businessId || isLoadingAI || hasUserInteracted || body) return

      // Cancel any existing request
      if (currentRequestRef.current) {
        currentRequestRef.current.abort()
      }

      // Create new abort controller
      currentRequestRef.current = new AbortController()
      lastRequestKeyRef.current = requestKey

      setIsLoadingAI(true)
      setAiError(null)

      try {
        const response = await fetch(`${process.env.VITE_AI_SERVICE_URL || 'https://booking-app-production-d357.up.railway.app'}/generate-replies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            business_id: businessId,
            conversation_id: conversationId,
            customer_id: customerId,
            email_address: to,
            message_type: 'email',
            recent_messages_count: 10
          }),
          signal: currentRequestRef.current.signal
        })

        if (!response.ok) {
          throw new Error(`Failed to generate suggestions: ${response.status}`)
        }

        const data = await response.json()
        if (data.suggestions && data.suggestions.length > 0) {
          // Use the first suggestion (usually professional tone)
          const suggestion = data.suggestions[0].text
          setAiSuggestion(suggestion)
          if (!hasUserInteracted && !body) {
            const html = formatBodyAsHtml(suggestion)
            setBody(html)
            if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = html
            setIsAiText(true)
          }
        }
      } catch (err) {
        // Don't show error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Error fetching AI suggestion:', err)
        setAiError(err instanceof Error ? err.message : 'Failed to get AI suggestion')
      } finally {
        setIsLoadingAI(false)
        currentRequestRef.current = null
      }
    }, 300) // 300ms debounce
  }, [businessId, conversationId, customerId, to, hasUserInteracted, body, isLoadingAI])

  // Use prefetched suggestion when available
  useEffect(() => {
    if (prefetchedSuggestion && !hasUserInteracted && !body) {
      setAiSuggestion(prefetchedSuggestion)
      setBody(prefetchedSuggestion)
      setIsAiText(true)
      return
    }
  }, [prefetchedSuggestion, hasUserInteracted, body])

  // Auto-fetch AI suggestion when component mounts or context changes (only if enabled and no prefetched suggestion)
  useEffect(() => {
    if (shouldFetchAI && businessId && !hasUserInteracted && !body && !prefetchedSuggestion) {
      console.log('🤖 EmailReplyComposer: Auto-fetching AI suggestions')
      debouncedFetchAISuggestion()
    } else if (!shouldFetchAI) {
      console.log('🤖 EmailReplyComposer: AI suggestions disabled')
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort()
      }
    }
  }, [shouldFetchAI, businessId, conversationId, customerId, to, prefetchedSuggestion]) // Fixed: Don't depend on the function itself

  const formatBodyAsHtml = (input: string) => {
    const escaped = input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
    const paragraphs = escaped.split(/\n{2,}/)
    const htmlParagraphs = paragraphs.map((paragraph) => {
      const withLineBreaks = paragraph.replace(/\n/g, '<br>')
      return `<p>${withLineBreaks}</p>`
    })
    return `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5;">${htmlParagraphs.join('')}</div>`
  }

  const stripHtmlForCheck = (html: string) =>
    (html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()

  const hasBodyContent = (b: string) => stripHtmlForCheck(b).length > 0

  const handleBodyFocus = () => {
    if (isAiText) {
      setIsAiText(false)
      setHasUserInteracted(true)
    }
  }

  const handleBodyClick = () => {
    if (isAiText) {
      setHasUserInteracted(true)
    }
  }

  const handleRefreshAI = async () => {
    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.abort()
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Reset states
    setHasUserInteracted(false)
    setIsAiText(false)
    setAiError(null)
    setBody('')
    
    // Create new abort controller
    currentRequestRef.current = new AbortController()
    setIsLoadingAI(true)

    try {
      const response = await fetch(`${process.env.VITE_AI_SERVICE_URL || 'https://booking-app-production-d357.up.railway.app'}/generate-replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          conversation_id: conversationId,
          customer_id: customerId,
          email_address: to,
          message_type: 'email',
          recent_messages_count: 10
        }),
        signal: currentRequestRef.current.signal
      })

      if (!response.ok) {
        throw new Error(`Failed to generate suggestions: ${response.status}`)
      }

      const data = await response.json()
      if (data.suggestions && data.suggestions.length > 0) {
        const suggestion = data.suggestions[0].text
        setAiSuggestion(suggestion)
        setBody(suggestion)
        setIsAiText(true)
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      console.error('Error refreshing AI suggestion:', err)
      setAiError(err instanceof Error ? err.message : 'Failed to get AI suggestion')
    } finally {
      setIsLoadingAI(false)
      currentRequestRef.current = null
    }
  }

  const handleSendEmail = async () => {
    if (!subject.trim() || !hasBodyContent(body)) {
      setError('Please enter both subject and message')
      return
    }

    if (!to) {
      setError('No email address specified')
      return
    }

    setIsSending(true)
    setError(null)

    try {
      // Convert attachments to base64 format
      const attachmentData = await Promise.all(
        attachments.map(async (file) => {
          return new Promise<{ filename: string; content: string; type: string }>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              const base64 = reader.result as string
              // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
              const base64Content = base64.split(',')[1] || base64
              resolve({
                filename: file.name,
                content: base64Content,
                type: file.type || 'application/octet-stream'
              })
            }
            reader.onerror = reject
            reader.readAsDataURL(file)
          })
        })
      )

      const signatureHtml = emailSignature && signatureIncluded
        ? `<p><br></p><p>${emailSignature.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</p>`
        : ''
      const bodyWithSignature = body.trim() + signatureHtml
      const result = await sendGmailEmail({
        businessId,
        to,
        cc: cc && cc.trim() ? cc.trim() : undefined,
        bcc: bcc && bcc.trim() ? bcc.trim() : undefined,
        subject: subject.trim(),
        body: bodyWithSignature,
        aiGenerated: isAiText,  // Pass the AI flag to the email sending function
        attachments: attachmentData.length > 0 ? attachmentData : undefined,
        threadId,
        inReplyTo,
        references
      })

      if (result.success) {
        const sentSubject = subject.trim()
        const sentBody = bodyWithSignature

        setSubject(defaultSubject)
        setBody('')
        if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = '<br>'
        setAttachments([])
        setIsAiText(false)
        setHasUserInteracted(false)
        setSignatureIncluded(true)
        
        if (onSendSuccess && result.messageId) {
          onSendSuccess(result.messageId, { subject: sentSubject, body: sentBody, to })
        }
      } else {
        const errorMessage = result.error || 'Failed to send email'
        setError(errorMessage)
        if (onSendError) {
          onSendError(errorMessage)
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email'
      setError(errorMessage)
      if (onSendError) {
        onSendError(errorMessage)
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const newFiles = Array.from(files)
    setAttachments(prev => [...prev, ...newFiles])
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (!isSending && subject.trim() && body.trim()) {
        handleSendEmail()
      }
    }
  }

  // Call the AI state change callback when AI state changes
  useEffect(() => {
    if (onAIStateChange) {
      onAIStateChange(isLoadingAI, aiError, isAiText);
    }
  }, [isLoadingAI, aiError, isAiText, onAIStateChange]);

  // Create a ref to expose the refresh function
  const refreshAIRef = useRef(handleRefreshAI);
  
  // Update ref when handler changes
  useEffect(() => {
    refreshAIRef.current = handleRefreshAI;
  }, [handleRefreshAI]);

  // Call onRefreshAI prop with the current refresh function
  useEffect(() => {
    if (onRefreshAI) {
      onRefreshAI(refreshAIRef.current);
    }
  }, [onRefreshAI]);

  // ─── Bottom toolbar layout ───
  if (toolbarPosition === 'bottom') {
    const isDisabled = disabled || isSending
    const canSend = !isDisabled && !!subject.trim() && hasBodyContent(body)

    return (
      <div className={`flex flex-col ${className}`}>
        {showSubjectInput && (
          <div className="flex-shrink-0">
            <input
              type="text"
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => !subjectReadOnly && setSubject(e.target.value)}
              readOnly={subjectReadOnly}
              className={`w-full border-0 border-b border-gray-200 px-4 py-2.5 text-base focus:outline-none focus:border-gray-300 ${subjectReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              style={{ fontSize: '16px' }}
              disabled={isDisabled || subjectReadOnly}
              onKeyDown={handleKeyDown}
            />
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto relative" style={{ WebkitOverflowScrolling: 'touch' as any }}>
          {isAIGenerating && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-md z-10 flex items-center justify-center">
              <div className="text-center">
                <div className="mb-3"><Sparkles className="h-8 w-8 text-blue-600 mx-auto animate-pulse" /></div>
                <p className="text-base font-medium text-gray-900 mb-2">Nexus</p>
                <p className="text-sm text-gray-500">Writing...</p>
              </div>
            </div>
          )}

          <div
            ref={bodyEditorRef}
            contentEditable={!isDisabled}
            suppressContentEditableWarning
            data-placeholder={isLoadingAI ? 'Getting AI suggestion...' : placeholder}
            className={`w-full px-4 py-3 focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 ${
              isAiText ? 'text-gray-500 italic' : 'text-gray-900'
            }`}
            style={{
              fontSize: '16px',
              minHeight: emailSignature && signatureIncluded ? '80px' : '100%',
              color: isAiText ? '#9CA3AF' : '#111827',
              fontStyle: isAiText ? 'italic' : 'normal',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: 1.6
            }}
            onInput={() => {
              const el = bodyEditorRef.current
              if (!el) return
              ignoreBodySyncRef.current = true
              let html = el.innerHTML
              if (html === '<br>' || html === '<br/>' || html === '<div><br></div>') html = ''
              setBody(html)
              setHasUserInteracted(true)
              setIsAiText(false)
            }}
            onFocus={handleBodyFocus}
            onClick={handleBodyClick}
          />

          {emailSignature && signatureIncluded && (
            <div className="relative px-4 pb-3">
              <div className="flex items-start gap-2">
                <div
                  className="text-sm text-gray-700 whitespace-pre-wrap flex-1"
                  style={{
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    lineHeight: 1.5,
                    fontSize: '14px'
                  }}
                >
                  {emailSignature}
                </div>
                <button
                  type="button"
                  onClick={() => setSignatureIncluded(false)}
                  className="flex-shrink-0 mt-0.5 text-red-400 hover:text-red-600 active:scale-95 transition-all touch-manipulation"
                  title="Remove signature from this email"
                >
                  <MinusCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {attachments.length > 0 && (
          <div className="flex-shrink-0 px-4 py-2 border-t border-gray-200 bg-gray-50/60">
            <div className="flex items-center gap-2 mb-1.5">
              <Paperclip className="h-3.5 w-3.5 text-gray-500" />
              <span className="text-xs font-medium text-gray-600">Attachments ({attachments.length})</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {attachments.map((file, index) => (
                <div key={index} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs">
                  <Paperclip className="h-3 w-3 text-gray-400" />
                  <span className="text-gray-700 truncate max-w-[150px]">{file.name}</span>
                  <button type="button" onClick={() => handleRemoveAttachment(index)} className="text-gray-400 hover:text-red-500 transition-colors ml-0.5" title="Remove">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="text-red-600 text-xs bg-red-50 px-4 py-2 border-t border-red-200 flex-shrink-0">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={handlePolish}
              disabled={isDisabled || isPolishing || !(body || '').trim()}
              className="flex items-center gap-1.5 pl-2.5 pr-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-all duration-200 active:scale-[0.95] touch-manipulation disabled:opacity-40 disabled:hover:bg-transparent"
              title="Polish — clean up what you've written"
            >
              {isPolishing ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
              <span className="text-sm font-semibold">Polish</span>
            </button>

            <div ref={formatAreaRef} className="relative">
              {showFormatBar && (
                <div
                  ref={formatBarRef}
                  className="absolute bottom-full left-0 mb-1.5 flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl shadow-lg p-1 z-20"
                >
                  <button type="button" onClick={() => applyFormat('bold')} className={`px-2.5 py-1.5 rounded-lg font-bold text-sm ${formatActive.bold ? 'bg-blue-100 text-blue-800' : 'text-gray-800 hover:bg-gray-100'}`} title="Bold">B</button>
                  <button type="button" onClick={() => applyFormat('italic')} className={`px-2.5 py-1.5 rounded-lg italic text-sm ${formatActive.italic ? 'bg-blue-100 text-blue-800' : 'text-gray-800 hover:bg-gray-100'}`} title="Italic">I</button>
                  <button type="button" onClick={() => applyFormat('underline')} className={`px-2.5 py-1.5 rounded-lg underline text-sm ${formatActive.underline ? 'bg-blue-100 text-blue-800' : 'text-gray-800 hover:bg-gray-100'}`} title="Underline">U</button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowFormatBar(prev => !prev)}
                disabled={isDisabled}
                className={`p-2.5 rounded-full transition-all duration-200 active:scale-[0.95] touch-manipulation ${
                  showFormatBar ? 'bg-gray-200 text-gray-800' : ''
                } ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'}`}
                title="Text formatting"
              >
                <Type className="h-5 w-5" />
              </button>
            </div>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isDisabled}
              className={`p-2.5 rounded-full transition-all duration-200 active:scale-[0.95] touch-manipulation ${
                isDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title="Attach file"
            >
              <Paperclip className="h-5 w-5" />
            </button>
          </div>

          <button
            onClick={handleSendEmail}
            disabled={!canSend}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 touch-manipulation ${
              !canSend
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg active:scale-[0.95]'
            }`}
            title={isSending ? 'Sending...' : 'Send'}
            aria-label="Send"
          >
            {isSending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>

        <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" />
      </div>
    )
  }

  // ─── Default inline toolbar layout ───
  return (
    <div className={className}>
      {!onAIStateChange && (
        <div className="flex items-center space-x-3 text-[10px] sm:text-xs py-1 min-h-[20px]">
          {(isLoadingAI || aiError || (isAiText && body)) && (
            <>
              <div className="flex items-center space-x-2">
                {isLoadingAI ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-purple-600 border-t-transparent"></div>
                    <span className="text-purple-600">Generating AI suggestion...</span>
                  </>
                ) : aiError ? (
                  <span className="text-red-500">AI suggestion failed</span>
                ) : isAiText ? (
                  <>
                    <Brain className="h-3 w-3 text-purple-600" />
                    <span className="text-purple-600">AI suggested reply</span>
                  </>
                ) : null}
              </div>
              
              {!isLoadingAI && (
                <button
                  onClick={handleRefreshAI}
                  className="text-purple-600 hover:text-purple-800 transition-colors"
                  title="Get new AI suggestion"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {showSubjectInput && (
        <div>
          <input
            type="text"
            placeholder="Email subject..."
            value={subject}
            onChange={(e) => !subjectReadOnly && setSubject(e.target.value)}
            readOnly={subjectReadOnly}
            className={variant === 'gmail'
              ? `w-full border-0 border-b border-gray-200 px-2 py-2 text-base focus:outline-none focus:border-gray-300 ${subjectReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`
              : `w-full border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${subjectReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            style={{ fontSize: '16px' }}
            disabled={disabled || isSending || subjectReadOnly}
            onKeyDown={handleKeyDown}
          />
        </div>
      )}

      <div className="relative w-full">
        {isAIGenerating && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-md z-10 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="mb-3">
                <Sparkles className="h-8 w-8 text-blue-600 mx-auto animate-pulse" />
              </div>
              <p className="text-base font-medium text-gray-900 mb-2">Nexus</p>
              <p className="text-sm text-gray-500">Writing...</p>
            </div>
          </div>
        )}
        
        <div
          ref={bodyEditorRef}
          contentEditable={!disabled && !isSending}
          suppressContentEditableWarning
          data-placeholder={isLoadingAI ? 'Getting AI suggestion...' : placeholder}
          className={variant === 'gmail'
            ? `w-full min-h-[300px] border-0 px-2 py-3 text-base focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 ${
                isAiText ? 'text-gray-500 italic' : 'text-gray-900'
              }`
            : `w-full min-h-[300px] border border-gray-300 pl-3 pr-12 py-2 text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 ${
                isAiText ? 'text-gray-500 italic' : 'text-gray-900'
              }`}
          style={{
            fontSize: '16px',
            color: isAiText ? '#9CA3AF' : '#111827',
            fontStyle: isAiText ? 'italic' : 'normal',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            lineHeight: 1.5
          }}
          onInput={() => {
            const el = bodyEditorRef.current
            if (!el) return
            ignoreBodySyncRef.current = true
            let html = el.innerHTML
            if (html === '<br>' || html === '<br/>' || html === '<div><br></div>') html = ''
            setBody(html)
            setHasUserInteracted(true)
            setIsAiText(false)
          }}
          onFocus={handleBodyFocus}
          onClick={handleBodyClick}
        />
        
        <button
          type="button"
          onClick={handlePolish}
          disabled={isDisabled || isPolishing || !(body || '').trim()}
          className="absolute bottom-2 right-28 p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-all duration-200 active:scale-[0.95] disabled:opacity-40 disabled:hover:bg-transparent"
          title="Polish — clean up what you've written"
        >
          {isPolishing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        </button>
        
        <div ref={formatAreaRef} className="absolute bottom-2 right-20 flex flex-col items-end">
          {showFormatBar && (
            <div
              ref={formatBarRef}
              className="absolute bottom-full right-0 mb-1 flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-20"
            >
              <button type="button" onClick={() => applyFormat('bold')} className={`p-1.5 rounded font-bold text-sm text-gray-800 ${formatActive.bold ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`} title="Bold">B</button>
              <button type="button" onClick={() => applyFormat('italic')} className={`p-1.5 rounded italic text-sm text-gray-800 ${formatActive.italic ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`} title="Italic">I</button>
              <button type="button" onClick={() => applyFormat('underline')} className={`p-1.5 rounded underline text-sm text-gray-800 ${formatActive.underline ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`} title="Underline">U</button>
            </div>
          )}
          <button
            type="button"
            onClick={() => setShowFormatBar(prev => !prev)}
            disabled={disabled || isSending}
            className={`p-2 rounded-full transition-all duration-200 active:scale-[0.95] ${
              showFormatBar ? 'bg-gray-200 text-gray-800' : ''
            } ${disabled || isSending ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100 cursor-pointer'}`}
            title="Text formatting"
          >
            <Type className="h-4 w-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isSending}
          className={`absolute bottom-2 right-12 p-2 rounded-full transition-all duration-200 active:scale-[0.95] ${
            disabled || isSending
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100 cursor-pointer'
          }`}
          title="Attach file"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        
        <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" />
        
        <button
          onClick={handleSendEmail}
          disabled={disabled || isSending || !subject.trim() || !hasBodyContent(body)}
          className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            disabled || isSending || !subject.trim() || !body.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer shadow-md hover:shadow-lg'
          }`}
          title={isSending ? 'Sending...' : isAiText ? 'Send AI suggested email' : 'Send Email'}
          aria-label="Send"
        >
          {isSending ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>

      {emailSignature && signatureIncluded && (
        <div className="relative mt-1 px-2">
          <div className="flex items-start gap-2">
            <div
              className="text-sm text-gray-700 whitespace-pre-wrap flex-1"
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                lineHeight: 1.5,
                fontSize: '14px'
              }}
            >
              {emailSignature}
            </div>
            <button
              type="button"
              onClick={() => setSignatureIncluded(false)}
              className="flex-shrink-0 mt-0.5 text-red-400 hover:text-red-600 active:scale-95 transition-all touch-manipulation"
              title="Remove signature from this email"
            >
              <MinusCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Paperclip className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Attachments ({attachments.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, index) => (
              <div key={index} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <Paperclip className="h-3 w-3 text-gray-500" />
                <span className="text-gray-700 truncate max-w-[200px]">{file.name}</span>
                <button type="button" onClick={() => handleRemoveAttachment(index)} className="text-gray-500 hover:text-red-600 transition-colors ml-1" title="Remove attachment">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="text-red-600 text-xs bg-red-50 p-2 rounded border border-red-200">
          {error}
        </div>
      )}
    </div>
  )
} 